import { useCallback, useMemo, useRef, useState } from 'react';
import { PiArrowFatLinesRightFill } from 'react-icons/pi';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import SideMsg from '#/modules/commonComponents/SideMsg';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import {
  buildStructMainColumns,
  buildStructSubColumns,
} from '#/modules/imsMgmt/settings/mtrStruct/data/structColumns';
import {
  structMainSideMsgBlocks,
  structSubSideMsgBlocks,
} from '#/modules/imsMgmt/settings/mtrStruct/data/structSideMsgConfig';
import { getMtrStructApiErrorMessage } from '#/modules/imsMgmt/settings/mtrStruct/helper/apiError';
import useMtrStructMainList from '#/modules/imsMgmt/settings/mtrStruct/hooks/useMtrStructMainList';
import useMtrStructSubList from '#/modules/imsMgmt/settings/mtrStruct/hooks/useMtrStructSubList';

const MtrStructListPage = () => {
  const mainListRef = useRef(null);
  const subListRef = useRef(null);
  const [selectedMainId, setSelectedMainId] = useState(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.MTR_CREATE);
  const canUpdate = useCan(PERM.MTR_UPDATE);

  const {
    rows: mainRows,
    pagination: mainPagination,
    isLoading: isMainLoading,
    isError: isMainError,
    error: mainError,
    refetch: refetchMain,
    listQuery: mainListQuery,
    setPage: setMainPage,
    setPageSize: setMainPageSize,
    setKeyword: setMainKeyword,
    setFilter: setMainFilter,
    removeSortAt: removeMainSortAt,
    resetListQuery: resetMainListQuery,
  } = useMtrStructMainList();

  const {
    rows: subRows,
    pagination: subPagination,
    isLoading: isSubLoading,
    isError: isSubError,
    error: subError,
    refetch: refetchSub,
    listQuery: subListQuery,
    setPage: setSubPage,
    setPageSize: setSubPageSize,
    setKeyword: setSubKeyword,
    setFilter: setSubFilter,
    removeSortAt: removeSubSortAt,
    resetListQuery: resetSubListQuery,
  } = useMtrStructSubList(selectedMainId);

  const mainColumns = useMemo(
    () => buildStructMainColumns({
      filters: mainListQuery.filters || {},
      onFilterChange: setMainFilter,
      canShowEdit: () => canUpdate,
    }),
    [mainListQuery.filters, setMainFilter, canUpdate],
  );

  const subColumns = useMemo(
    () => buildStructSubColumns({
      filters: subListQuery.filters || {},
      onFilterChange: setSubFilter,
      canShowEdit: () => canUpdate,
    }),
    [subListQuery.filters, setSubFilter, canUpdate],
  );

  const mainFilterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(mainColumns),
    [mainColumns],
  );

  const subFilterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(subColumns),
    [subColumns],
  );

  const sideMsgBlocks = useMemo(() => {
    if (displayData?._source === 'sub') {
      return structSubSideMsgBlocks;
    }

    return structMainSideMsgBlocks;
  }, [displayData?._source]);

  const subCreatePath = useMemo(() => {
    const base = ROUTES.mtrStructSubCreate;

    if (!selectedMainId) {
      return base;
    }

    return `${base}?mtrStructMainId=${encodeURIComponent(selectedMainId)}`;
  }, [selectedMainId]);

  const handleMainRowClick = useCallback((row, isSelected) => {
    subListRef.current?.clearSelection?.();

    if (isSelected) {
      setSelectedMainId(row.original.id);
      setSelectedData({ ...row.original, _source: 'main' });
      return;
    }

    setSelectedMainId(null);
    setSelectedData(null);
  }, [setSelectedData]);

  const handleSubRowClick = useCallback((row, isSelected) => {
    mainListRef.current?.clearSelection?.();

    if (isSelected) {
      setSelectedData({ ...row.original, _source: 'sub' });
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const mainHeaderAction = canCreate ? (
    <ListAddButton to={ROUTES.mtrStructMainCreate} />
  ) : null;

  const subHeaderAction = canCreate ? (
    <ListAddButton to={subCreatePath} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">物料結構</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <div
              className="
                grid h-full min-h-0 grid-cols-1 gap-4
                @3xl/table-panel:grid-cols-[1fr_2rem_1fr]
              "
            >
              <div className="flex h-full min-h-0 flex-col gap-2">
                <p className="shrink-0 text-sm font-medium text-muted-foreground">主分類</p>
                <ListTablePanel
                  ref={mainListRef}
                  tableKey="ims.mtr_struct.main.list"
                  panelClassName="min-h-0 flex-1"
                  columns={mainColumns}
                  rows={mainRows}
                  pagination={mainPagination}
                  isLoading={isMainLoading}
                  isError={isMainError}
                  errorMessage={
                    isMainError ? getMtrStructApiErrorMessage(mainError, '載入列表失敗') : undefined
                  }
                  onRetry={isMainError ? () => { refetchMain(); } : undefined}
                  onPageChange={setMainPage}
                  onPageSizeChange={setMainPageSize}
                  onRowClick={handleMainRowClick}
                  onSearch={setMainKeyword}
                  onReset={resetMainListQuery}
                  initialKeyword={mainListQuery.keyword}
                  searchPlaceholder="搜尋主分類代號或名稱"
                  headerAction={mainHeaderAction}
                  listQuery={mainListQuery}
                  filterStatusFields={mainFilterStatusFields}
                  sortFieldLabels={DEFAULT_LIST_SORT_FIELD_LABELS}
                  onFilterChange={setMainFilter}
                  onKeywordClear={() => setMainKeyword('')}
                  onSortRemove={removeMainSortAt}
                />
              </div>

              <div className="hidden justify-self-center self-center @3xl/table-panel:flex">
                <PiArrowFatLinesRightFill
                  className="
                    flex aspect-square items-center justify-center
                    rounded-full bg-primary-400 p-1 text-white
                  "
                  size={28}
                />
              </div>

              <div className="flex h-full min-h-0 flex-col gap-2">
                <p className="shrink-0 text-sm font-medium text-muted-foreground">子分類</p>
                <ListTablePanel
                  ref={subListRef}
                  tableKey="ims.mtr_struct.sub.list"
                  panelClassName="min-h-0 flex-1"
                  columns={subColumns}
                  rows={subRows}
                  pagination={subPagination}
                  isLoading={isSubLoading}
                  isError={isSubError}
                  errorMessage={
                    isSubError ? getMtrStructApiErrorMessage(subError, '載入列表失敗') : undefined
                  }
                  onRetry={isSubError ? () => { refetchSub(); } : undefined}
                  onPageChange={setSubPage}
                  onPageSizeChange={setSubPageSize}
                  onRowClick={handleSubRowClick}
                  onSearch={setSubKeyword}
                  onReset={resetSubListQuery}
                  initialKeyword={subListQuery.keyword}
                  searchPlaceholder={selectedMainId ? '搜尋子分類' : '請先選擇主分類'}
                  headerAction={subHeaderAction}
                  listQuery={subListQuery}
                  filterStatusFields={subFilterStatusFields}
                  sortFieldLabels={DEFAULT_LIST_SORT_FIELD_LABELS}
                  onFilterChange={setSubFilter}
                  onKeywordClear={() => setSubKeyword('')}
                  onSortRemove={removeSubSortAt}
                />
              </div>
            </div>
          </div>

          <SideMsg
            title={displayData?._source === 'sub' ? '子分類' : '主分類'}
            titleValue="name"
            displayData={displayData}
            blocks={sideMsgBlocks}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default MtrStructListPage;
